import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { DocumentItem } from '../document-models/document-item';
import { DocumentComponent } from '../document-models/documentComponent';
import { FrenchAccentNormalizer } from 'src/app/utility/Objects/frenchNormalizationMap';
import { DirectoryService } from 'src/services/directory.service';
import { DocumentService } from 'src/services/document.service';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { CpsAlphaBarComponent } from '../cps-alpha-bar/cps-alpha-bar.component';
import AlphaBarUtils from '../cps-alpha-bar/alpha-bar-utils';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-cps-directory-document',
  templateUrl: './cps-directory-document.component.html',
  styleUrls: ['./cps-directory-document.component.scss']
})
export class CpsDirectoryDocumentComponent implements OnInit, DocumentComponent, OnDestroy {
  @Input() htmlElement: HTMLElement;
  @ViewChild('docWrapper') documentWrapperElement: ElementRef;
  @ViewChild('alphaBar') alphaBar: CpsAlphaBarComponent;
  @ViewChildren('allTheseManufacturers') manufacturerListElement: QueryList<any>;

  public doc: DocumentItem;
  public document: Document;

  public list;
  public identifier;
  public fan: FrenchAccentNormalizer;
  public currentLetter = 'a';
  public showID: string;
  public directoryDetails: string;
  public directorySet: Map<string, string>;
  public alphabarSelectedChar = 'a';

  private headingIdPrefix: string;
  public contentLoaded = false;
  public isParentPageLoaded = false;

  public isScrollLocked = false; 

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  // Search variables
  public searchText: string;
  public hasSearchBeenMade: boolean;
  public currentFocusIndex: number;
  public totalMatchesNum: number;
  public highlightList = [];
  public titlesList = [];

  public alphas = []; 

  private alphaChangeSubject = new Subject<string>();

  private isScrollingToLetter = false;

  constructor(
    public directoryService: DirectoryService,
    private documentService: DocumentService,
    public viewportScroller: ViewportScroller,
    private store: Store<any>
  ) {
    this.documentService.setContentLoaded(false);
    this.fan = new FrenchAccentNormalizer();
    this.directorySet = new Map<string, string>();
    this.directoryDetails = '';
  }

  ngOnInit(): void {
    this.headingIdPrefix = '#' + this.doc.id;
    this.viewportScroller.scrollToPosition([0, 0]);

    this.onSearchTextChanged(this.doc.searchText);
    this.onSearchIndexChange(0);
    this.searchText = this.doc.searchText;

    switch (this.doc.id) {
      case 'manufacturers':
        this.identifier = 'manufacturers';

        if (DirectoryService.manufacturers === undefined) {
          this.directoryService.getManufacturers()
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
              DirectoryService.manufacturers = value;
              this.list = value.organizationinfo;
              this.titlesList = value.organizationinfo;
              DirectoryService.manufacturers.titleList = DirectoryService.manufacturers.organizationinfo.map(info => info.title);
              DirectoryService.keywordSearchList.set(this.doc.id, this.list);
              this.excludeNonParticipating();
              this.contentLoaded = true;
            });
        } else {
          this.list = DirectoryService.manufacturers.organizationinfo;
          this.list = this.list.filter(item => item.participating !== 'no');
          setTimeout(() => {
            this.contentLoaded = true;
          }, 500);
        }
        break;

      case 'healthorgs':
        this.identifier = 'healthorgs';

        if (DirectoryService.healthorgs === undefined) {
          this.directoryService.getHealthOrgs()
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
              DirectoryService.healthorgs = value;
              this.list = value.organizationinfo;
              DirectoryService.healthorgs.titleList = DirectoryService.healthorgs.organizationinfo.map(info => info.title);
              DirectoryService.keywordSearchList.set(this.doc.id, this.list);
              this.contentLoaded = true;
            });
        } else {
          this.list = DirectoryService.healthorgs.organizationinfo;
          setTimeout(() => {
            this.contentLoaded = true;
          }, 500);
        }
        break;
    }

    this.alphaChangeSubject
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(selectedLetter => {
        this.handleAlphaChange(selectedLetter); 
      });
  }

  ngAfterViewInit(): void {
    this.isParentPageLoaded = true;  // Mark loaded ASAP
    this.documentService.setContentLoaded(true);

    this.manufacturerListElement.changes.subscribe(() => {
      this.isParentPageLoaded = true;
      this.documentService.setContentLoaded(true);
    });
  }

  isANumber(letter: string): string {
    if (isNaN(Number(letter))) {
      return letter;
    }
    return '#';
  }

  specialCharacterReplace(text: string): string {
    return text.replace('&amp;', '&');
  }

  normalizeTitle(title: string): string {
    return this.fan.frenchAccentNormalizer(title);
  }

  newHeading(letter: string): boolean {

    const l = this.isANumber(letter);


    if (this.alphabarSelectedChar.toLowerCase() === l.toLowerCase()) {
      Promise.resolve().then(() => {
        this.alphabarSelectedChar = '';
      });
      return true;
    }

    if (this.currentLetter !== l) {
      this.currentLetter = l;
      return true;
    }
    return false;
  }

  showItem(id: string): void {
    if (this.showID === id) {
      this.showID = '';
    } else {
      this.showID = id;
    }
  }

  onSelectedAlphaChange(selectedLetter: string): void {
    if (!!this.alphaBar) {
      this.alphaBar.isScrolling = true; 
      this.alphaChangeSubject.next(selectedLetter);
      setTimeout(() => {
        this.alphaBar.isScrolling = false; 
      }, 1500);
    } 
  }

  private handleAlphaChange(selectedLetter: string): void {

    if (this.isScrollingToLetter) return;

    if (this.isParentPageLoaded) {
      const headerId = this.headingIdPrefix + selectedLetter;
      const headerElement = document.querySelector(headerId);

      if (headerElement) {
        this.isScrollingToLetter = true;

        this.currentLetter = selectedLetter;
        this.alphabarSelectedChar = selectedLetter;

        const headerPosition = headerElement.getBoundingClientRect().top + document.documentElement.scrollTop;
        const scrollOffsetForHeader = AlphaBarUtils.getScrollOffsetForWindowWidth();
        console.log("Scrolling to position: " + (headerPosition - scrollOffsetForHeader)); 
        window.scrollTo({
          top: headerPosition - scrollOffsetForHeader,
          behavior: 'smooth'
        });

        setTimeout(() => {
          this.isScrollingToLetter = false;
        }, 800);
      }
    } 
  }

  getDirectoryDetail(id: string): void {
    this.directoryDetails = '';

    if (this.directorySet.has(id)) {
      this.directoryDetails = this.directorySet.get(id);
    } else {
      this.directoryService.getDirectoryDetail(id, this.identifier)
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (value == null || value === '') {
            this.directoryDetails = '';
          } else {
            this.directorySet.set(id, value);
            this.directoryDetails = value;
          }
        },
          (error) => {
            this.directoryDetails = '';
          });
    }
  }

  checkContentLoaded(): void {
    this.documentService.getContentLoaded()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoaded => {
        this.contentLoaded = isLoaded;
        this.excludeNonParticipating();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.doc.searchText = this.searchText;
  }

  onSearchTextChanged = (text: string): number => {
    this.searchText = text;
    if (this.searchText && this.searchText.length > 2) {
      this.highlightList = [];
      this.hasSearchBeenMade = true;

      DirectoryService.keywordSearchTerm.set(this.doc.id, this.searchText);

      DirectoryService[this.doc.id].titleList.forEach(title => {
        if (title.toLowerCase().includes(this.searchText.toLowerCase())) {
          this.highlightList.push(title);
        }
      });

      this.totalMatchesNum = this.highlightList.length;
      this.currentFocusIndex = 0;
    } else if (!this.searchText) {
      this.clearSearch();
    }
    return this.totalMatchesNum;
  }

  clearSearch(): void {
    this.highlightList = [];
    this.searchText = '';
    this.currentFocusIndex = -1;
    this.totalMatchesNum = 0;
    this.hasSearchBeenMade = false;
    this.viewportScroller.scrollToPosition([0, 0]);
    DirectoryService.keywordSearchTerm.set(this.doc.id, '');
  }

  onSearchIndexChange = (increment: number): number => {
    this.currentFocusIndex = increment;

    if (this.currentFocusIndex < 0) {
      this.currentFocusIndex = 0;
    }

    if (this.currentFocusIndex > this.highlightList.length - 1) {
      this.currentFocusIndex = 0;
    }

    if (this.highlightList.length > 0) {
      this.setSelectedLetter(this.highlightList[this.currentFocusIndex][0]);
      this.scrollToHighlightedText(this.highlightList[this.currentFocusIndex]);
    }
    return this.currentFocusIndex;
  }

  scrollToHighlightedText(id: string): void {
    this.viewportScroller.setOffset([0, 500]);
    this.viewportScroller.scrollToAnchor(id);
  }

  setSelectedLetter(selectedLetter: string): void {
    this.currentLetter = selectedLetter;
    this.alphabarSelectedChar = selectedLetter;
    this.alphaBar.alphaSelected(selectedLetter);
  }

  nextChar(c: string): string {
    return c === 'z' ? null : c === 'Z' ? null : String.fromCharCode(c.charCodeAt(0) + 1);
  }

  excludeNonParticipating(): void {
    this.list = this.list.filter(item => item.participating !== 'no');
  }
}